import AuthedImg from "./AuthedImg";
import type { SocialPost } from "../social";

type Props = {
  year: number;
  month: number;
  posts: SocialPost[];
  selected?: string | null;
  onMonth: (year: number, month: number) => void;
  onDay: (iso: string) => void;
  onOpen: (post: SocialPost) => void;
};

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const DAYS = ["L", "M", "M", "J", "V", "S", "D"];

function iso(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export function postClock(post: SocialPost): string {
  const raw = post.scheduled_at || post.sent_at || "";
  const match = raw.match(/T(\d{2}:\d{2})/);
  return match ? match[1] : "";
}

export function postTitle(post: SocialPost): string {
  const line = (post.caption || "").split("\n")[0].trim();
  return line || "Sans texte";
}

function thumbPath(post: SocialPost): string {
  const media = post.media?.[0];
  if (!media || media.kind !== "image") return "";
  return media.preview || media.url || "";
}

export default function PublishCalendar({ year, month, posts, selected, onMonth, onDay, onOpen }: Props) {
  const first = new Date(year, month, 1);
  const start = (first.getDay() + 6) % 7;
  const count = new Date(year, month + 1, 0).getDate();
  const today = iso(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
  const byDay: Record<string, SocialPost[]> = {};
  posts.forEach((p) => {
    const key = p.day || (p.scheduled_at || p.sent_at || "").slice(0, 10);
    if (!key) return;
    byDay[key] = byDay[key] || [];
    byDay[key].push(p);
  });
  Object.values(byDay).forEach((list) => list.sort((a, b) => postClock(a).localeCompare(postClock(b))));
  return (
    <section className="pr-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <button type="button" className="pr-cal-nav" aria-label="Mois précédent" onClick={() => (month === 0 ? onMonth(year - 1, 11) : onMonth(year, month - 1))}>
          ‹
        </button>
        <h2 className="text-lg font-semibold capitalize">
          {MONTHS[month]} {year}
        </h2>
        <button type="button" className="pr-cal-nav" aria-label="Mois suivant" onClick={() => (month === 11 ? onMonth(year + 1, 0) : onMonth(year, month + 1))}>
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-slate-400">
        {DAYS.map((d, i) => (
          <span key={i} className="py-1">
            {d}
          </span>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {Array.from({ length: start }, (_, i) => (
          <span key={`e${i}`} />
        ))}
        {Array.from({ length: count }, (_, i) => {
          const day = iso(year, month, i + 1);
          const list = byDay[day] || [];
          return (
            <div
              key={day}
              className={`min-h-16 rounded-2xl border p-1 ${
                day === selected ? "border-violet bg-mist" : day === today ? "border-violet" : "border-slate-100"
              }`}
            >
              <button type="button" className="text-sm font-semibold" onClick={() => onDay(day)}>
                {i + 1}
              </button>
              <div className="mt-1 space-y-1">
                {list.slice(0, 2).map((post) => {
                  const thumb = thumbPath(post);
                  return (
                    <button key={post.id} type="button" className="flex w-full items-center gap-1 text-left" onClick={() => onOpen(post)}>
                      <span className="hidden h-5 w-5 shrink-0 overflow-hidden rounded bg-white sm:block">
                        {thumb ? <AuthedImg path={thumb} alt="" className="h-5 w-5 object-cover" /> : null}
                      </span>
                      <span className="min-w-0 truncate text-[10px]">
                        {postClock(post)} {postTitle(post)}
                      </span>
                    </button>
                  );
                })}
                {list.length > 2 ? (
                  <button type="button" className="block text-[10px] text-slate-500" onClick={() => onOpen(list[0])}>
                    +{list.length - 2}
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
